<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <DateBar
          :dateRange="localDateRange"
          @updateRange="
            (value, startDate, index) => {
              localDateRange = index;
              $emit('update-dates', 'startDate', startDate);
            }
          "
          format-date="YYYY-MM-DD"
        />
        <DateFilters
          :initialDate="startDate"
          :finalDate="endDate"
          @updateInitialDate="$emit('update-dates', 'startDate', $event)"
          @updateFinalDate="$emit('update-dates', 'endDate', $event)"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-lg pa-4 card" flat>
          <v-card-title>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field
                  ref="button-search"
                  v-model="table.filters.search"
                  :label="$t('transactions.search')"
                  outlined
                  dense
                  clearable
                  append-icon="search"
                  hide-details
                  class="rounded-lg"
                  @click:append="$emit('get-transactions')"
                  @keyup.enter="$emit('get-transactions')"
                  @click:clear="$emit('clear-search')"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-autocomplete
                  v-model="table.filters.organization"
                  :items="organizations"
                  :label="$t('transactions.dashboard.organization')"
                  item-value="id"
                  item-text="name"
                  dense
                  outlined
                  class="rounded-lg"
                  hide-details
                  clearable
                  @change="$emit('get-transactions')"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="table.filters.state"
                  :items="states"
                  :label="$t('transactions.dashboard.status')"
                  item-value="value"
                  item-text="name"
                  dense
                  outlined
                  class="rounded-lg"
                  hide-details
                  clearable
                  @change="$emit('get-transactions')"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-autocomplete
                  v-model="table.filters.gateway"
                  :items="gateways"
                  label="Gateway"
                  item-value="name"
                  item-text="display_name"
                  class="rounded-lg"
                  dense
                  outlined
                  hide-details
                  clearable
                  @change="$emit('get-transactions')"
                />
              </v-col>
              <v-col cols="12" md="3" class="d-flex justify-end">
                <v-btn
                  ref="button-open-export-dialog"
                  @click="$emit('active-export-dialog')"
                  color="#2661FF"
                >
                  <v-icon color="white" left>cloud_download</v-icon>
                  <span style="color: white">{{ $t("general.export") }}</span>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-data-table
            ref="table-donations"
            :headers="table.headers"
            :items="table.items"
            :options="table.pagination"
            :footer-props="{ 'items-per-page-options': [5, 10, 25, 50, 100] }"
            :items-per-page="table.pagination.itemsPerPage"
            :server-items-length="table.total"
            :loading="loading"
            :no-data-text="$t('transactions.dashboard.empty')"
            :no-results-text="$t('transactions.dashboard.empty_search')"
            @update:options="$emit('table-pagination', $event)"
            @click:row="$emit('open-details', $event)"
          >
            <template v-slot:item.created_at="{ item }">
              <span class="body-2">
                {{ item.created_at | moment("LL") }}
              </span>
            </template>
            <template v-slot:item.lead.first_name="{ item }">
              <span class="body-2">
                {{ formatLeadName(item.lead) }}
              </span>
            </template>
            <template v-slot:item.conversion_amount="{ item }">
              <span class="body-2">
                {{
                  formatCurrencyCode(
                    item.amount,
                    item.gateway.currency.symbol,
                    item.gateway.currency.code
                  )
                }}
              </span>
            </template>
            <template v-slot:item.state="{ item }">
              <v-icon :class="getStateColor(item.state)">{{
                getStatusIcon(item.state)
              }}</v-icon>
              <span class="subtitle-2" :class="getStateColor(item.state)">
                {{ getStateByNumber(item.state) }}
              </span>
            </template>
            <template v-slot:item.organization.name="{ item }">
              <span class="subtitle-2">
                {{ formatOrganizationName(item.organization) }}
              </span>
            </template>
            <template v-slot:item.payment_method="{ item }">
              <span class="body-2">
                {{ getPaymentMethodById(item.payment_method).text }}
              </span>
            </template>
            <template v-slot:item.gateway.name="{ item }">
              <span class="body-2">
                {{ formatGatewayName(item.gateway) }}
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import { mapState, mapMutations, mapGetters, mapActions } from "vuex";
import DateFilters from "@/components/Common/DateFilters";
import DateBar from "@/components/Common/DateBar";
import donationMixin from "@/mixins/donations";
import BaseToolbar from "@/components/BaseToolbar";

export default {
  components: {
    DateFilters,
    DateBar,
    BaseToolbar
  },
  mixins: [donationMixin],
  props: {
    loading: {
      type: Boolean,
      required: true,
      default: false
    },
    table: {
      type: Object,
      required: true,
      default: () => {}
    },
    startDate: {
      type: String,
      required: true,
      default: new Date()
    },
    endDate: {
      type: String,
      required: true,
      default: new Date()
    },
    organizations: {
      type: Array,
      required: true,
      default: () => []
    },
    states: {
      type: Array,
      required: true,
      default: () => []
    },
    gateways: {
      type: Array,
      required: true,
      default: () => []
    },
    dateRange: {
      type: Number,
      required: false,
      default: 6
    }
  },
  data() {
    return {
      localDateRange: 3
    };
  },
  created() {
    this.localDateRange = this.dateRange;
  },
  computed: {
    ...mapState({
      updateRequired: ({ transactions: { updateRequired } }) => updateRequired
    })
  },
  watch: {
    async updateRequired() {
      await this.$emit("get-transactions");
      this.setUpdateRequired(false);
    }
  },
  methods: {
    ...mapMutations({
      setUpdateRequired: "transactions/setUpdateRequired"
    }),
    getStatusIcon(state) {
      const SUCCESS = this.getStateValueByName("success");
      const ACTIVE = this.getStateValueByName("active");

      switch (state) {
        case SUCCESS:
        case ACTIVE:
          return "done";
        default:
          return "warning";
      }
    },
    formatLeadName(lead) {
      return lead ? `${lead.first_name} ${lead.last_name}` : "-";
    },
    formatOrganizationName(organization) {
      return organization ? organization.name : "-";
    },
    formatGatewayName(gateway) {
      const PAYMENTS_WAY = "Bakery";
      const RECURRENT_PAYMENTS = "Recurrent Payments";

      if (gateway.gateway) {
        const { name } = gateway.gateway;

        switch (name) {
          case PAYMENTS_WAY:
            return "Payments Way";
          case RECURRENT_PAYMENTS:
            return RECURRENT_PAYMENTS;
          default:
            return "-";
        }
      }
    },
    formatCurrencyCode(amount, symbol, code) {
      if (amount && symbol && code) {
        return `${symbol} ${amount} ${code}`;
      } else if (amount && symbol) {
        return `${symbol} ${amount}`;
      } else if (amount) {
        return amount;
      } else {
        return "-";
      }
    }
  }
};
</script>

<style>
.card {
  border: 1px solid #d0d5dd !important;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.15) !important;
}

.header_color {
  color: #2661ff !important;
}
</style>
